import React, { useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
  FormControl,
  InputAdornment,
  IconButton,
  Input,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { DialogContext, JoySnackBarContext } from "./context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import debounce from "lodash.debounce";
import { styled } from "@mui/system";
import Validator from "fastest-validator";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DoneOutlineIcon from "@mui/icons-material/DoneOutline";

const config = require("../config/config");

const v = new Validator();

const otpSchema = {
  otp: {
    type: "string",
    length: 6,
    pattern: /^[0-9]{6}$/,
    messages: {
      stringPattern: "The OTP must be a 6-digit number.",
      stringLength: "The OTP must be exactly 6 digits long.",
    },
  },
};

const passwordSchema = {
  password: {
    type: "string",
    min: 8,
    max: 14,
    pattern:
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,14}$/,
    messages: {
      stringMin: "Password must be at least 8 characters long",
      stringMax: "Password must not be longer than 14 characters",
      stringPattern:
        "Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character",
    },
  },
};

// Create custom styles using styled
const DialogActionsMobile = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
}));

const DialogActionsWeb = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

function ForgotPasswordDialog({ forgotPassword }) {
  const { DialogDispatcher } = React.useContext(DialogContext);
  const { JoySnackBarDispatcher } = React.useContext(JoySnackBarContext);

  const [step, setStep] = useState(1);
  const [contactInfo, setContactInfo] = useState("");
  const [contactInfoErrors, setContactInfoErrors] = useState([]);
  const [otp, setOtp] = useState("");
  const [otpErrors, setOtpErrors] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordErrors, setNewPasswordErrors] = useState([]);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState("");

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleClose = () => {
    DialogDispatcher("OPEN", { forgotPassword: false });
    setStep(1);
    setContactInfo("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleNext = async () => {
    setLoading(true);
    if (step === 1) {
      try {
        const response = await axios.post(
          `${config.baseURL}/auth/forgotpassword`,
          {
            identifier: contactInfo,
          }
        );
        if (response.status === 200) {
          JoySnackBarDispatcher("OPEN", {
            vertical: "top",
            horizontal: "right",
            message: response.data.snackAlertMessage,
            color: "success",
            size: "md",
            variant: "soft",
            iconName: "LoginSuccessful",
            iconColor: "success",
          });

          setStep(2);
        }
      } catch (error) {
        console.error("Error sending OTP:", error);
        setContactInfoErrors(["Failed to send OTP, please try again."]);
      }
    } else if (step === 2) {
      const validationResponse = v.validate({ otp }, otpSchema);
      if (validationResponse !== true) {
        setOtpErrors(validationResponse.map((err) => err.message));
      } else {
        setOtpErrors([]);
        try {
          const response = await axios.post(
            `${config.baseURL}/auth/forgot-password-otp-verify`,
            { identifier: contactInfo, otp }
          );
          if (response.status === 200) {
            setResetKey(response.data.passwordResetKey);
            JoySnackBarDispatcher("OPEN", {
              vertical: "top",
              horizontal: "right",
              message: response.data.snackAlertMessage,
              color: "success",
              size: "md",
              variant: "soft",
              iconName: "LoginSuccessful",
              iconColor: "success",
            });

            setStep(3);
          }
        } catch (error) {
          console.error("Error verifying OTP:", error);
          setOtpErrors(["Invalid OTP, please try again."]);
        }
      }
    } else if (step === 3) {
      setLoading(false);
      const validationResponse = v.validate(
        { password: newPassword },
        passwordSchema
      );
      if (validationResponse !== true) {
        setNewPasswordErrors(validationResponse.map((err) => err.message));
        return;
      }

      if (newPassword !== confirmPassword) {
        setConfirmPasswordErrors(["Passwords do not match"]);
      } else {
        setConfirmPasswordErrors([]);
        try {
          const response = await axios.post(
            `${config.baseURL}/auth/reset-password`,
            {
              password: newPassword,
              identifier: contactInfo,
              resetKey: resetKey,
            }
          );
          if (response.status === 200) {
            JoySnackBarDispatcher("OPEN", {
              vertical: "top",
              horizontal: "right",
              message: response.data.snackAlertMessage,
              color: "success",
              size: "md",
              variant: "soft",
              iconName: "LoginSuccessful",
              iconColor: "success",
            });
            handleClose();
          }
        } catch (error) {
          console.error("Error resetting password:", error);
          setNewPasswordErrors(["Failed to reset password, please try again."]);
        }
      }
    }
    setLoading(false);
  };

  const debouncedHandleContactInfoChange = useCallback(
    debounce((newContactInfo) => {
      if (!newContactInfo) {
        setContactInfoErrors(["Please enter a valid identifier."]);
      } else {
        setContactInfoErrors([]);
      }
    }, 400),
    []
  );

  const handleContactInfoChange = (e) => {
    const newContactInfo = e.target.value;
    setContactInfo(newContactInfo);
    debouncedHandleContactInfoChange(newContactInfo);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  // Handle "Enter" key press to trigger the Next button
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleNext();
    }
  };

  return (
    <Dialog open={forgotPassword} onClose={handleClose} fullScreen={fullScreen}>
      <DialogTitle>Forgot Password</DialogTitle>
      <DialogContent>
        {step === 1 && (
          <>
            <DialogContentText sx={{ marginBottom: "1.4vh" }}>
              Please enter your Username, Email, or Phone (without country code)
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Username, Email or Phone"
              type="text"
              fullWidth
              value={contactInfo}
              onChange={handleContactInfoChange}
              error={contactInfoErrors.length > 0}
              helperText={contactInfoErrors.join(", ")}
              onKeyDown={handleKeyDown} // Use onKeyDown instead of onKeyPress
            />
          </>
        )}
        {step === 2 && (
          <>
            <DialogContentText sx={{ marginBottom: "1.4vh" }}>
              An OTP has been sent. Please enter it below.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="OTP"
              type="text"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              error={otpErrors.length > 0}
              helperText={otpErrors.join(", ")}
              onKeyDown={handleKeyDown} // Use onKeyDown instead of onKeyPress
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleNext}
                      disabled={otpErrors.length > 0 || otp === ""}
                    >
                      <DoneOutlineIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}
        {step === 3 && (
          <>
            <DialogContentText>
              Please enter your new password.
            </DialogContentText>
            <FormControl
              required
              error={newPasswordErrors.length > 0}
              fullWidth
              margin="dense"
            >
              <InputLabel htmlFor="new-password">New Password</InputLabel>
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={handleKeyDown} // Use onKeyDown instead of onKeyPress
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText>{newPasswordErrors.join(", ")}</FormHelperText>
            </FormControl>
            <FormControl
              required
              error={confirmPasswordErrors.length > 0}
              fullWidth
              margin="dense"
            >
              <InputLabel htmlFor="confirm-password">
                Confirm Password
              </InputLabel>
              <Input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={handleKeyDown} // Use onKeyDown instead of onKeyPress
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowConfirmPassword}>
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText>
                {confirmPasswordErrors.join(", ")}
              </FormHelperText>
            </FormControl>
          </>
        )}
        {fullScreen ? (
          <DialogActionsMobile>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="error"
              sx={{ mr: (theme) => theme.spacing(1) }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              color="success"
              disabled={loading} // Keep enabled even when passwords don't match
            >
              {step === 3 ? "Submit" : "Next"}
            </Button>
          </DialogActionsMobile>
        ) : (
          <DialogActionsWeb>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="error"
              sx={{ mr: (theme) => theme.spacing(1) }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              color="success"
              disabled={loading} // Keep enabled even when passwords don't match
            >
              {step === 3 ? "Submit" : "Next"}
            </Button>{" "}
          </DialogActionsWeb>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ForgotPasswordDialog;
